import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

export default function EnableFeature({ dev }) {
  const VALID_FEATURES = [
    { value: 'parchisiRoadConfigs', label: 'Parchisi Road Configs' },
    { value: 'petRaceConfigs', label: 'Pet Race Configs'}, 
    { value: 'treasureTalesConfig', label: 'Treasure Tales Configs'},
    { value: 'spEventCenterTable', label: 'Special Parchisi Event'},
    { value: 'seasonConfigs', label: 'Season Pass Configs'},
    { value: 'cardCollectionConfig', label: 'Rush Hour' },
    { value: 'debugTokenPosition', label: 'Token Position Team Up'},
    { value: 'dailyPeriodicConfig', label: 'Big Win Lock Unlock' },
    { value: 'someSale', label: 'Some Sale' }
  ];

  const AB_EXPERIMENTS = [
    { value: 'streakbonus', label: 'Streak Bonus' },
    { value: 'parchisiRoad', label: 'Parchisi Road'},
    { value: 'petRace', label: 'Pet Race'},
    { value: 'treasureTales', label: 'Treasure Tales' },
    { value: 'dailyMissions', label: 'Daily Missions' },
    { value: 'comboSale', label: 'Combo Sale' },
    { value: 'fortuneRoom', label: 'Fortune Room' },
    { value: 'inGameCosmetics', label: 'InGameCosmetics' },
    { value: 'superUndo', label: 'SuperUndo' },
    { value: 'magicPick', label: 'Magic Pick' }
  ];

  const FEATURE_KEY_MAP = {
    'parchisiRoadConfigs':'parchisi_road',
    'petRaceConfigs': 'pet_race',
    'treasureTalesConfig': 'treasure_tales',
    'spEventCenterTable': 'sp_event_center',
    'seasonConfigs': 'season',
  };

  const [activeLiveOpsData, setActiveLiveOpsData] = useState(null);
  const [featureId, setFeatureId] = useState(VALID_FEATURES[0].value);
  const [abFeatureId, setAbFeatureId] = useState(AB_EXPERIMENTS[0].value);
  const [version, setVersion] = useState('');
  const [availableVersions, setAvailableVersions] = useState([]);
  const [abUserId, setAbUserId] = useState('');
  const [abStatus, setAbStatus] = useState(1);
  useEffect(() => {
    if (featureId === 'cardCollectionConfig' || featureId === 'debugTokenPosition' || featureId === 'dailyPeriodicConfig' || featureId === 'someSale') {
      setAvailableVersions([]);
      setVersion('');
      return;
    }
    const fetchVersions = async () => {
      try {
        const response = await fetch(`/api/dev/${dev}/featureVersions?featureId=${featureId}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok && data.versions) {
          setAvailableVersions(data.versions);
          if (data.versions.length > 0) {
            setVersion(data.versions[0]);
          } else {
            setVersion('');
          }
        } else {
          setAvailableVersions([]);
          setVersion('');
        }
      } catch (err) {
        console.error("Failed to fetch feature versions:", err);
      }
    };
    fetchVersions();
  }, [featureId, dev]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configData, setConfigData] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tokenPositions, setTokenPositions] = useState({ 0: '', 1: '', 2: '', 3: ''});
  const [nextStartDate, setNextStartDate] = useState(null);
  const [testStartDate, setTestStartDate] = useState(null);
  const [someSaleLatestId, setSomeSaleLatestId] = useState(null);
  const [fetchedFeatureId, setFetchedFeatureId] = useState(null);
  const [fetchedVersion, setFetchedVersion] = useState(null);

  const handleFetchConfig = async () => {
    if (!featureId.trim()) {
      setError('Please enter a Feature ID');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setConfigData(null);

    try {
      const params = new URLSearchParams({ featureId });
      if (version.trim() && featureId !== 'cardCollectionConfig' && featureId !== 'debugTokenPosition' && featureId !== 'dailyPeriodicConfig' && featureId !== 'someSale') {
        params.append('version', version);
      }

      const response = await fetch(`/api/dev/${dev}/featureConfig?${params.toString()}`, {
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch config');
      }

      setConfigData(data);
      setFetchedFeatureId(featureId);
      setFetchedVersion(version);

      // Helper to parse the UTC string and treat it as a local time for the date picker
      const parseUTCAsLocal = (utcString) => {
        if (!utcString) return null;
        return new Date(utcString.replace('Z', ''));
      };

      setStartDate(null);
      setEndDate(null);
      setNextStartDate(null);
      setTestStartDate(null);
      setSomeSaleLatestId(null);
      if (featureId == 'spEventCenterTable' || featureId == 'treasureTalesConfig') {
        if (data.start_time) setStartDate(parseUTCAsLocal(data.start_time));
        if (data.end_time) setEndDate(parseUTCAsLocal(data.end_time));
      }
      else if (featureId == 'seasonConfigs') {
        if (data.duration_info?.start_date) setStartDate(parseUTCAsLocal(data.duration_info.start_date));
        if (data.duration_info?.end_date) setEndDate(parseUTCAsLocal(data.duration_info.end_date));
      }
      else if(featureId == 'cardCollectionConfig') {
        if (data.start_date) setStartDate(parseUTCAsLocal(data.start_date));
        if (data.end_date) setEndDate(parseUTCAsLocal(data.end_date));
        if (data.next_start_date) setNextStartDate(parseUTCAsLocal(data.next_start_date));
      } 
      else if(featureId === 'debugTokenPosition'){
        if (data.default_position) {
          setTokenPositions({
            0: data.default_position["0"] ?? '',
            1: data.default_position["1"] ?? '',
            2: data.default_position["2"] ?? '',
            3: data.default_position["3"] ?? ''
          })
        }
      }
      else if(featureId === 'someSale') {
        let latestId = null;
        let latestDateMs = -Infinity;
        const now = new Date();
        if (data.configs) {
          for (const key of Object.keys(data.configs)) {
            const configObj = data.configs[key];
            if (configObj && configObj.start_date) {
              const startDate = new Date(configObj.start_date.replace('Z', ''));
              const ms = startDate.getTime();
              if (ms <= now.getTime() && ms > latestDateMs) {
                latestDateMs = ms;
                latestId = key;
              }
            }
          }
        }
        if (latestId && data.configs[latestId]) {
          setSomeSaleLatestId(latestId);
          const lv = data.configs[latestId];
          if (lv.start_date) setStartDate(parseUTCAsLocal(lv.start_date));
          if (lv.test_start_date) setTestStartDate(parseUTCAsLocal(lv.test_start_date));
          
          if (!data.active_version) {
            data.active_version = [parseInt(latestId)];
          } else if (!data.active_version.includes(parseInt(latestId))) {
            data.active_version = [parseInt(latestId), ...data.active_version];
          }
        }
      }
      else{
        if(data.prod_dates?.start_date) setStartDate(parseUTCAsLocal(data.prod_dates.start_date));
        if(data.prod_dates?.end_date) setEndDate(parseUTCAsLocal(data.prod_dates.end_date));
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async (bigWinEnable = null) => {
    if (featureId !== fetchedFeatureId || version !== fetchedVersion) {
      setError('You have not fetched the config for the selected feature and version yet. Please click "Fetch Configuration" first.');
      window.scrollTo(0, 0);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const formatAsUTC = (dateObj) => {
      if (!dateObj) return null;
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      const seconds = String(dateObj.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
    };

    try {
      let updatedConfig = { ...configData };

      if (featureId == 'spEventCenterTable' || featureId == 'treasureTalesConfig'){
        updatedConfig = {
          ...updatedConfig,
          start_time: startDate ? formatAsUTC(startDate) : configData.start_time,
          end_time: endDate ? formatAsUTC(endDate) :configData.end_time,
          };
        } 
        else if(featureId == 'seasonConfigs'){
          updatedConfig = {
            ...updatedConfig,
            duration_info: {
              ...configData.duration_info,
              start_date: startDate ? formatAsUTC(startDate) : configData.duration_info?.start_date,
              end_date: endDate ? formatAsUTC(endDate) : configData.duration_info?.end_date,
            }
          };
        }
        else if (featureId == 'cardCollectionConfig') {
        updatedConfig = {
          ...updatedConfig,
          start_date: startDate ? formatAsUTC(startDate) : configData.start_date,
          end_date: endDate ? formatAsUTC(endDate) : configData.end_date,
            next_start_date: nextStartDate ? formatAsUTC(nextStartDate) : configData.next_start_date,
        };
        } 
        else if(featureId === 'debugTokenPosition') {
          updatedConfig = {
            ...updatedConfig,
            default_position: {
              ...configData.default_position,
              "0": tokenPositions[0] !== '' ? Number(tokenPositions[0]) : configData.default_position?.["0"],
              "1": tokenPositions[1] !== '' ? Number(tokenPositions[1]) : configData.default_position?.["1"],
              "2": tokenPositions[2] !== '' ? Number(tokenPositions[2]) : configData.default_position?.["2"],
              "3": tokenPositions[3] !== '' ? Number(tokenPositions[3]) : configData.default_position?.["3"]
            }
          };
        } else if (featureId === 'dailyPeriodicConfig') {
          const newDuration = bigWinEnable === true ? 86400000000000 : 3600000000000;
          const targetSlot = '00:30:00Z';
          const updateDpe = (dpeObj) => {
            if (!dpeObj) return dpeObj;
            let newDpeObj = { ...dpeObj };
            for (const day of ['0', '1', '2', '3', '4', '5', '6']) {
              if (newDpeObj[day] && Array.isArray(newDpeObj[day])) {
                newDpeObj[day] = newDpeObj[day].map(item => {
                  if (item.start && item.start.includes(targetSlot)) {
                    return { ...item, duration: newDuration };
                  }
                  return item;
                });
              }
            }
            return newDpeObj;
          };
          updatedConfig = {
            ...updatedConfig,
            dpeConfig: updateDpe(updatedConfig.dpeConfig),
            dpeConfigV2: updateDpe(updatedConfig.dpeConfigV2),
            dpeConfigV3: updateDpe(updatedConfig.dpeConfigV3),
            dpeConfigV4: updateDpe(updatedConfig.dpeConfigV4),
          };
        } else if (featureId === 'someSale') {
          updatedConfig = { ...updatedConfig };
          if (someSaleLatestId && updatedConfig.configs && updatedConfig.configs[someSaleLatestId]) {
            updatedConfig.configs[someSaleLatestId] = {
              ...updatedConfig.configs[someSaleLatestId],
              start_date: startDate ? formatAsUTC(startDate) : updatedConfig.configs[someSaleLatestId].start_date,
              test_start_date: testStartDate ? formatAsUTC(testStartDate) : updatedConfig.configs[someSaleLatestId].test_start_date
            };
          }
        } else {
          updatedConfig = {
            ...updatedConfig,
            prod_dates: {
              ...configData.prod_dates,
              start_date: startDate ? formatAsUTC(startDate) : configData.prod_dates?.start_date,
              end_date: endDate ? formatAsUTC(endDate) : configData.prod_dates?.end_date,
            }
          };
        }

      const response = await fetch(`/api/dev/${dev}/featureConfig`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          featureId,
          version: (featureId === 'cardCollectionConfig' || featureId === 'debugTokenPosition' || featureId === 'dailyPeriodicConfig' || featureId === 'someSale') ? undefined : (version || undefined),
          config: updatedConfig,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update config');
      }

      setSuccess('Configuration updated successfully!');
      setConfigData(updatedConfig);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

    const handleFetchActiveLiveOps = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ featureId: 'activeLiveOps' });
      const response = await fetch(`/api/dev/${dev}/featureConfig?${params.toString()}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch activeLiveOps');
      
      setActiveLiveOpsData(data);
      setSuccess('Fetched Active Live Ops configuration!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateVersion = async () => {
    if (!activeLiveOpsData || !version.trim()) {
      setError('Please fetch Active Live Ops first and ensure a Version is entered.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const liveOpsKey = FEATURE_KEY_MAP[featureId];
      if (!liveOpsKey) {
        throw new Error(`Mapping not found for ${featureId} in Active Live Ops.`);
      }

      const existingArray = activeLiveOpsData[liveOpsKey] || [];
      const newVersion = version.toString();

      let newArray = [...existingArray];
      if (newArray[0] !== newVersion) {
        newArray = newArray.filter(v => v !== newVersion);
        newArray.unshift(newVersion);
      }

      const updatedLiveOps = {
        ...activeLiveOpsData,
        [liveOpsKey]: newArray 
      }

      const response = await fetch(`/api/dev/${dev}/featureConfig`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          featureId: 'activeLiveOps',
          config: updatedLiveOps, 
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save activeLiveOps');

      setActiveLiveOpsData(updatedLiveOps);
      setSuccess(`Version ${version} successfully registered in activeLiveOps!`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Enable Feature
            {dev && (
              <span style={{ fontSize: '0.6em', color: '#666', marginLeft: '10px' }}>
                [{dev}]
              </span>
            )}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel id="feature-select-label">Feature</InputLabel>
                <Select
                  labelId="feature-select-label"
                  value={featureId}
                  label="Feature"
                  onChange={(e) => setFeatureId(e.target.value)}
                >
                  {VALID_FEATURES.map((feature) => (
                    <MenuItem key={feature.value} value={feature.value}>
                      {feature.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={loading || featureId === 'cardCollectionConfig' || featureId === 'debugTokenPosition' || featureId === 'someSale'}>
                <InputLabel id="version-select-label">Version</InputLabel>
                <Select
                  labelId="version-select-label"
                  value={version}
                  label="Version"
                  onChange={(e) => setVersion(e.target.value)}
                >
                  {featureId === 'cardCollectionConfig' || featureId === 'debugTokenPosition' || featureId === 'someSale' ? (
                    <MenuItem value="" disabled>
                      Not Applicable
                    </MenuItem>
                  ) : availableVersions.length > 0 ? (
                    availableVersions.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No versions found
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFetchConfig}
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : 'Fetch Configuration'}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 3 }}>
              {success}
            </Alert>
          )}

          {configData && (
            <Box sx={{ mt: 4 }}>
              <Paper elevation={2} sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  Configuration
                </Typography>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Current Configuration (Read-Only):
                  </Typography>
                  <pre style={{
                    background: '#fff',
                    padding: '16px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '300px',
                    fontSize: '12px'
                  }}>
                    {JSON.stringify(configData, null, 2)}
                  </pre>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                  {featureId === 'debugTokenPosition' ? 'Edit Default Token Positions' : (featureId === 'dailyPeriodicConfig' ? 'Big Win Actions' : 'Edit Dates')}
                </Typography>

                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {featureId === 'debugTokenPosition' ? (
                    <>
                      {[0, 1, 2, 3].map((pos) => (
                        <Grid item xs={12} md={3} key={pos}>
                          <TextField
                            label={`Token ${pos}`}
                            type="number"
                            value={tokenPositions[pos]}
                            onChange={(e) => setTokenPositions({ ...tokenPositions, [pos] : e.target.value})}
                            fullWidth
                            disabled={loading}
                          />
                        </Grid>
                      ))}
                    </>
                  ) : featureId === 'dailyPeriodicConfig' ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleUpdateConfig(true)}
                          disabled={loading}
                          fullWidth
                        >
                          {loading ? <CircularProgress size={24} color="inherit" /> : 'Enable (24hr Duration)'}
                        </Button>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleUpdateConfig(false)}
                          disabled={loading}
                          fullWidth
                        >
                          {loading ? <CircularProgress size={24} color="inherit" /> : 'Disable (1hr Duration)'}
                        </Button>
                      </Grid>
                    </>
                  ) : (
                  <>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      disabled={loading}
                    />
                  </Grid>
                  {featureId !== 'someSale' && (
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      disabled={loading}
                    />
                  </Grid>
                  )}
                  {featureId === 'someSale' && (
                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Test Start Date"
                        value={testStartDate}
                        onChange={setTestStartDate}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                        disabled={loading}
                      />
                    </Grid>
                  )}
                  {featureId === 'cardCollectionConfig' && (
                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Next Start Date"
                        value={nextStartDate}
                        onChange={setNextStartDate}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                        disabled={loading}
                      />
                    </Grid>
                  )}
                  </>
                  )}
                  {featureId !== 'dailyPeriodicConfig' && (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleUpdateConfig(null)}
                        disabled={loading}
                        fullWidth
                      >
                        {loading ? <CircularProgress size={24} /> : 'Update Configuration'}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>
          )}
        </Paper>

        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4, bgcolor: '#e3f2fd' }}>
            <Typography variant="h5" gutterBottom>
              Register in Active Live Ops
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Activate the selected module version (Currently: <strong>{version || 'None'}</strong>). This will prepend the new version to the top of the list, keeping older versions intact.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleFetchActiveLiveOps}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : 'Fetch activeLiveOps.json'}
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleActivateVersion}
                  disabled={loading || !activeLiveOpsData || !version.trim()}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : 'Insert  & Activate Version'}
                </Button>
              </Grid>
            </Grid>

            {activeLiveOpsData && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Live Ops Preview:
                </Typography>
                <pre style={{
                  background: '#fff',
                  padding: '16px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px',
                  fontSize: '12px'
                }}>
                  {JSON.stringify(activeLiveOpsData, null, 2)}
                </pre>
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              A/B Experimentation
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={loading}>
                  <InputLabel id="ab-feature-select-label">Experiment</InputLabel>
                  <Select
                    labelId="ab-feature-select-label"
                    value={abFeatureId}
                    label="Experiment"
                    onChange={(e) => setAbFeatureId(e.target.value)}
                  >
                    {AB_EXPERIMENTS.map((exp) => (
                      <MenuItem key={exp.value} value={exp.value}>
                        {exp.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {abFeatureId && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Override Cohort Status
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="User ID"
                      value={abUserId}
                      onChange={(e) => setAbUserId(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="gr-status-label">Cohort Status</InputLabel>
                      <Select
                        labelId="gr-status-label"
                        value={abStatus}
                        label="Cohort Status"
                        onChange={(e) => setAbStatus(e.target.value)}
                      >
                        <MenuItem value={0}>Disabled</MenuItem>
                        <MenuItem value={1}>Enabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="warning"
                      fullWidth
                      disabled={loading}
                      onClick={async () => {
                        if (!abUserId.trim()) { setError('Please enter a valid User ID'); return; }
                        setLoading(true);
                        setError(''); setSuccess('');
                        try {
                          const response = await fetch(`/api/dev/${dev}/setABExperiment/${abUserId}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ status: abStatus, featureId: abFeatureId }),
                          });
                          
                          const rawText = await response.text();
                          let data;
                          try { data = JSON.parse(rawText); } 
                          catch (e) { throw new Error(`Server returned non-JSON: ${rawText}`); }

                          if (data.err) throw new Error(data.err);
                          setSuccess(`Experiment status updated for user: ${abUserId}!`);
                        } catch (err) { setError(err.message); } 
                        finally { setLoading(false); }
                      }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Override User'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    </LocalizationProvider>
  );
}